import React from 'react';
import './Sidebar.css';

function handleLogout() {
  localStorage.removeItem('pnp_access_token');
  localStorage.removeItem('pnp_id_token');
  localStorage.removeItem('pnp_refresh_token');
  localStorage.removeItem('pnp_subscriber_session');
  localStorage.removeItem('pnp_onboarded_clients');
  localStorage.removeItem('pnp_all_sessions');
  window.location.href = '/login';
}

/**
 * PNP Operator Sidebar — The management dashboard navigation.
 *
 * Access: Only for Admins and PNP Operators.
 * Menus: Onboard Client, List Clients, View Client, Update/Remove, Launch Application.
 *
 * This sidebar is NEVER shown in the Client Portal view.
 * When "Launch Application" is clicked, the entire layout switches to
 * ClientPortalSidebar (physically absent from this component's DOM).
 */
const Sidebar = ({
  activeModule,
  setActiveModule,
  clientConfig,
  isOnboarded,
}) => {

  // --- Build dynamic menus from client config ---
  const buildClientModules = () => {
    if (!clientConfig) return [];

    const modules = [];
    const product = (clientConfig.product_name || '').toLowerCase();
    const tenantType = (clientConfig.tenant_type || 'standalone').toLowerCase();

    // Dashboard is always first after onboarding
    modules.push({ id: 'dashboard', icon: '📊', label: 'Dashboard' });
    modules.push({ id: 'things-to-do', icon: '📋', label: 'Things To Do' });

    // LLM-driven modules — derived from the client's product/industry
    // These map to what LLM config defines as queryable use cases
    if (product.includes('restaurant') || product.includes('hospitality') || product.includes('food') || product.includes('dining')) {
      modules.push(
        { id: 'orders', icon: '🍽️', label: 'Orders & Sales' },
        { id: 'inventory', icon: '📦', label: 'Inventory' },
        { id: 'reservations', icon: '📅', label: 'Reservations' },
        { id: 'catering', icon: '🎪', label: 'Catering & Events' },
        { id: 'staff', icon: '👥', label: 'Staff' },
      );
    } else if (product.includes('fleet') || product.includes('transport') || product.includes('logistics')) {
      modules.push(
        { id: 'fleet', icon: '🚛', label: 'Fleet Tracker' },
        { id: 'routes', icon: '🗺️', label: 'Routes' },
        { id: 'drivers', icon: '👥', label: 'Drivers' },
        { id: 'maintenance', icon: '🔧', label: 'Maintenance' },
      );
    } else if (product.includes('patient') || product.includes('health') || product.includes('clinic') || product.includes('medical')) {
      modules.push(
        { id: 'patients', icon: '🏥', label: 'Patients' },
        { id: 'appointments', icon: '📅', label: 'Appointments' },
        { id: 'records', icon: '📋', label: 'Records' },
        { id: 'billing', icon: '💳', label: 'Billing' },
      );
    } else if (product.includes('retail') || product.includes('store') || product.includes('shop')) {
      modules.push(
        { id: 'pos', icon: '🛒', label: 'Point of Sale' },
        { id: 'inventory', icon: '📦', label: 'Inventory' },
        { id: 'customers', icon: '👥', label: 'Customers' },
        { id: 'promotions', icon: '🏷️', label: 'Promotions' },
      );
    } else if (product.includes('bank') || product.includes('financial') || product.includes('qrm') || product.includes('risk')) {
      modules.push(
        { id: 'balance_sheet', icon: '🏦', label: 'Balance Sheet' },
        { id: 'deposits', icon: '💰', label: 'Deposits & Behavior' },
        { id: 'loans', icon: '📄', label: 'Loan Portfolio' },
        { id: 'cashflows', icon: '💸', label: 'Cashflow Engine' },
        { id: 'risk', icon: '⚠️', label: 'Risk Metrics' },
      );
    } else {
      // Generic fallback for any product type
      modules.push(
        { id: 'operations', icon: '⚙️', label: 'Operations' },
        { id: 'data', icon: '📊', label: 'Business Data' },
        { id: 'reports', icon: '📋', label: 'Reports' },
      );
    }

    // Parent/franchise owners get a chain-wide view
    if (tenantType === 'parent') {
      modules.push({ id: 'chain', icon: '🏗️', label: 'Chain Overview' });
    }

    // --- PNP Operator Modules (management dashboard only) ---

    // LLM Ask Me — private BI queries against the client's database
    modules.push({ id: 'askme', icon: '💬', label: 'Ask Me (LLM)' });

    // Comm-AI — public-facing MCP/A2A bridge
    modules.push({ id: 'agentic', icon: '🐙', label: 'Comm-AI' });

    // Permission Logs — view/revoke cross-tenant access grants
    modules.push({ id: 'permissions', icon: '🔐', label: 'Permission Logs' });

    // Command Center — operator controls
    modules.push({ id: 'command', icon: '🎛️', label: 'Command Center' });

    // --- Client Management (PNP Operator only) ---
    modules.push({ id: 'divider', icon: '', label: '── Client Management ──', isDivider: true });
    modules.push({ id: 'admin-dashboard', icon: '🛡️', label: 'Admin Dashboard' });
    modules.push({ id: 'list-clients', icon: '📋', label: 'List Clients' });
    modules.push({ id: 'view-client', icon: '🔍', label: 'View Client' });
    modules.push({ id: 'update-client', icon: '✏️', label: 'Update Client' });
    modules.push({ id: 'remove-client', icon: '🗑️', label: 'Remove Client' });
    modules.push({ id: 'launch-app', icon: '▶️', label: 'Launch Client-Application' });

    return modules;
  };

  const preOnboardModules = [
    { id: 'onboard', icon: '🚀', label: 'Onboard Client', alwaysVisible: true },
    { id: 'things-to-do', icon: '📋', label: 'Things To Do', alwaysVisible: true },
    { id: 'admin-dashboard', icon: '🛡️', label: 'Admin Dashboard', alwaysVisible: true },
    { id: 'list-clients', icon: '📋', label: 'List Clients', alwaysVisible: true },
    { id: 'view-client', icon: '🔍', label: 'View Client', alwaysVisible: true },
    { id: 'update-client', icon: '✏️', label: 'Update Client', alwaysVisible: true },
    { id: 'remove-client', icon: '🗑️', label: 'Remove Client', alwaysVisible: true },
    { id: 'launch-app', icon: '▶️', label: 'Launch Client-Application', alwaysVisible: true },
  ];

  const visibleModules = isOnboarded ? buildClientModules() : preOnboardModules;

  return (
    <aside className="sidebar">
      <div className="logo">
        <h2>PNP Infra</h2>
        {!isOnboarded && <span className="operator-badge">Operator Mode</span>}
        {isOnboarded && clientConfig && (
          <span className="client-badge">{clientConfig.company_name || 'Client'}</span>
        )}
      </div>

      <nav className="nav">
        {visibleModules.map(module => (
          module.isDivider ? (
            <div key={module.id} className="nav-divider" aria-hidden="true">
              <span className="divider-label">{module.label}</span>
            </div>
          ) : (
            <button
              key={module.id}
              className={`nav-item ${activeModule === module.id ? 'active' : ''}`}
              onClick={() => setActiveModule(module.id)}
              aria-current={activeModule === module.id ? 'page' : undefined}
            >
              <span className="icon">{module.icon}</span>
              <span className="label">{module.label}</span>
            </button>
          )
        ))}

        {!isOnboarded && (
          <div className="locked-hint">
            <span className="lock-icon">🔒</span>
            <span>Complete onboarding to unlock all modules</span>
          </div>
        )}
      </nav>

      {isOnboarded && clientConfig && (
        <div className="client-info">
          <div className="client-info-row">
            <span className="info-label">Plan</span>
            <span className="info-value">{clientConfig.plan || 'starter'}</span>
          </div>
          <div className="client-info-row">
            <span className="info-label">Type</span>
            <span className="info-value">{clientConfig.tenant_type || 'standalone'}</span>
          </div>
          <button
            className="nav-item onboard-another"
            onClick={() => setActiveModule('onboard')}
          >
            <span className="icon">➕</span>
            <span className="label">Onboard Another</span>
          </button>
        </div>
      )}

      <div className="sidebar-footer">
        <button
          className="nav-item logout-btn"
          onClick={handleLogout}
          aria-label="Logout"
        >
          <span className="icon">🚪</span>
          <span className="label">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
